import React, { useEffect, useState } from "react";
import client from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Trash2, BookOpen, ScrollText, Compass, Search, Share2, Link as LinkIcon, X } from "lucide-react";

const KIND_META = {
  verse: { icon: BookOpen, label: "Verse", color: "text-[#2E4F4F]" },
  dictionary: { icon: ScrollText, label: "Dictionary", color: "text-[#9A3B3B]" },
  topic: { icon: Compass, label: "Topic", color: "text-[#78350F]" },
  word: { icon: Search, label: "Word", color: "text-stone-700" },
  syntax: { icon: BookOpen, label: "Syntax", color: "text-stone-700" },
};

export default function Studies() {
  const nav = useNavigate();
  const { user, loading } = useAuth();
  const [studies, setStudies] = useState([]);
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/login"); return; }
    client.get("/studies")
      .then((r) => setStudies(r.data))
      .catch(() => toast.error("Failed to load studies"))
      .finally(() => setBusy(false));
  }, [user, loading, nav]);

  const del = async (id) => {
    if (!window.confirm("Delete this study?")) return;
    try {
      await client.delete(`/studies/${id}`);
      setStudies((s) => s.filter((x) => x.id !== id));
      toast.success("Deleted");
    } catch { toast.error("Delete failed"); }
  };

  const shareUrl = (tok) => `${window.location.origin}/share/${tok}`;

  const enableShare = async (id) => {
    try {
      const r = await client.post(`/studies/${id}/share`);
      setStudies((s) => s.map((x) => x.id === id ? { ...x, share_token: r.data.share_token } : x));
      await navigator.clipboard?.writeText(shareUrl(r.data.share_token)).catch(() => {});
      toast.success("Public link created & copied");
    } catch { toast.error("Share failed"); }
  };

  const revokeShare = async (id) => {
    try {
      await client.delete(`/studies/${id}/share`);
      setStudies((s) => s.map((x) => x.id === id ? { ...x, share_token: null } : x));
      toast.success("Sharing revoked");
    } catch { toast.error("Failed"); }
  };

  const copyLink = async (tok) => {
    try {
      await navigator.clipboard.writeText(shareUrl(tok));
      toast.success("Link copied");
    } catch { toast.error("Copy failed"); }
  };

  return (
    <div className="space-y-10" data-testid="studies-page">
      <div>
        <div className="text-xs uppercase tracking-[0.3em] text-[#78350F] mb-4">My Studies</div>
        <h1 className="font-serif-display text-5xl md:text-6xl text-stone-900 mb-3">Your library.</h1>
        <p className="text-stone-600 max-w-2xl">Verses, dictionary entries, and topical outlines you've set aside for further study.</p>
      </div>

      {busy && <div className="text-stone-500">Loading...</div>}

      {!busy && studies.length === 0 && (
        <div className="border hairline bg-[#FDFBF7] p-12 text-center" data-testid="studies-empty">
          <div className="font-serif-display text-2xl text-stone-800 mb-3">Nothing saved yet.</div>
          <p className="text-sm text-stone-600 mb-6">Save verses, word entries, or topical outlines to gather them here.</p>
          <div className="flex justify-center gap-4">
            <Link to="/word" className="text-sm text-[#9A3B3B] underline underline-offset-4">Start a word search</Link>
            <Link to="/verse" className="text-sm text-[#9A3B3B] underline underline-offset-4">Look up a verse</Link>
          </div>
        </div>
      )}

      <div className="space-y-4">
        {studies.map((s) => {
          const meta = KIND_META[s.kind] || KIND_META.word;
          const Icon = meta.icon;
          return (
            <div key={s.id} className="border hairline bg-[#FDFBF7] p-6 flex items-start justify-between gap-4"
                 data-testid={`study-${s.id}`}>
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <Icon size={14} strokeWidth={1.5} className={meta.color} />
                  <span className={`text-xs uppercase tracking-[0.2em] ${meta.color}`}>{meta.label}</span>
                  <span className="text-xs text-stone-400 font-mono">{new Date(s.created_at).toLocaleDateString()}</span>
                  {s.share_token && (
                    <span className="text-[10px] uppercase tracking-wider text-[#2E4F4F] bg-[#2E4F4F]/10 px-2 py-0.5" data-testid={`shared-badge-${s.id}`}>
                      Public
                    </span>
                  )}
                </div>
                <div className="font-serif-display text-2xl text-stone-900">{s.title}</div>
                {s.kind === "verse" && s.payload?.text && (
                  <p className="text-sm text-stone-700 mt-2 italic">"{s.payload.text}"</p>
                )}
                {s.kind === "dictionary" && s.payload?.core_meaning && (
                  <p className="text-sm text-stone-700 mt-2 italic">{s.payload.core_meaning}</p>
                )}
                {s.kind === "topic" && s.payload?.summary && (
                  <p className="text-sm text-stone-700 mt-2 italic">{s.payload.summary}</p>
                )}
                {s.share_token && (
                  <div className="mt-3 flex items-center gap-2 text-xs">
                    <LinkIcon size={12} strokeWidth={1.5} className="text-stone-400" />
                    <button onClick={() => copyLink(s.share_token)}
                      data-testid={`copy-share-${s.id}`}
                      className="font-mono text-stone-500 hover:text-[#9A3B3B] truncate max-w-[380px]">
                      {shareUrl(s.share_token)}
                    </button>
                  </div>
                )}
              </div>
              <div className="flex flex-col items-end gap-2">
                {s.share_token ? (
                  <button onClick={() => revokeShare(s.id)} data-testid={`revoke-${s.id}`}
                    title="Revoke public link"
                    className="text-stone-400 hover:text-[#9A3B3B] flex items-center gap-1 text-xs">
                    <X size={14} strokeWidth={1.5} /> Unshare
                  </button>
                ) : (
                  <button onClick={() => enableShare(s.id)} data-testid={`share-${s.id}`}
                    title="Create public link"
                    className="text-stone-500 hover:text-[#2E4F4F] flex items-center gap-1 text-xs">
                    <Share2 size={14} strokeWidth={1.5} /> Share
                  </button>
                )}
                <button onClick={() => del(s.id)} data-testid={`del-${s.id}`}
                  className="text-stone-400 hover:text-[#9A3B3B]">
                  <Trash2 size={16} strokeWidth={1.5} />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
